import { z } from 'zod';
import { esquemaId } from './comunes.js';
import { problemasDeObservacion } from './observacion.js';
import { evaluarCondicion } from './plantilla.js';
/**
 * Documento clinico generico (F2.6, plan maestro §6.4 y §13.2).
 *
 * Guarda las respuestas a UNA version concreta de una plantilla, con la huella
 * de esa version. La firma cubre las respuestas y las referencias de version:
 * publicar otra version de la plantilla, o retirarla, no cambia ni la forma ni
 * la firma de lo ya firmado (AC-09).
 *
 * Borrador incompleto permitido; la firma exige los campos marcados como
 * `requeridoParaFirmar` que esten visibles (plan §5.4).
 */
export const ESTADOS_DE_DOCUMENTO_CLINICO = ['borrador', 'firmado'];
const esquemaRespuestas = z.record(z.string().max(40), z.unknown());
export const esquemaCrearDocumentoClinico = z
    .object({
    pacienteId: esquemaId,
    encuentroId: esquemaId.optional(),
    plantillaId: z.string().min(3).max(120),
    plantillaVersion: z.number().int().min(1),
    respuestas: esquemaRespuestas.default({}),
})
    .strict();
export const esquemaActualizarDocumentoClinico = z
    .object({ versionEsperada: z.number().int().min(0), respuestas: esquemaRespuestas })
    .strict();
export const esquemaFirmarDocumentoClinico = z
    .object({ versionEsperada: z.number().int().min(0) })
    .strict();
const PATRON_FECHA = /^\d{4}-\d{2}-\d{2}$/;
const vacio = (valor) => valor === undefined ||
    valor === null ||
    valor === '' ||
    (Array.isArray(valor) && valor.length === 0);
/** Problemas del valor de UN campo, segun su tipo. `null` si esta bien. */
function problemaDeValor(campo, valor) {
    switch (campo.tipo) {
        case 'texto':
        case 'texto-largo':
            if (typeof valor !== 'string')
                return 'debe ser texto';
            return valor.length > campo.maxLongitud
                ? `admite hasta ${campo.maxLongitud} caracteres`
                : null;
        case 'numero':
            if (typeof valor !== 'number' || !Number.isFinite(valor))
                return 'debe ser un número';
            if (campo.min !== undefined && valor < campo.min)
                return `el mínimo es ${campo.min}`;
            if (campo.max !== undefined && valor > campo.max)
                return `el máximo es ${campo.max}`;
            return null;
        case 'fecha':
            return typeof valor === 'string' && PATRON_FECHA.test(valor) ? null : 'use AAAA-MM-DD';
        case 'booleano':
            return typeof valor === 'boolean' ? null : 'debe ser sí o no';
        case 'seleccion':
            return campo.opciones.some((o) => o.valor === valor) ? null : 'opción desconocida';
        case 'seleccion-multiple': {
            if (!Array.isArray(valor))
                return 'debe ser una lista';
            if (new Set(valor).size !== valor.length)
                return 'opciones repetidas';
            return valor.every((v) => campo.opciones.some((o) => o.valor === v))
                ? null
                : 'opción desconocida';
        }
        case 'estado-informacion':
            return campo.admite.includes(valor) ? null : 'valor no admitido';
        case 'observacion': {
            if (!valor || typeof valor !== 'object')
                return 'debe ser una medición';
            const medicion = valor;
            const problemas = problemasDeObservacion({
                codigo: campo.codigo,
                unidad: medicion.unidad,
                valor: medicion.valor,
                componentes: medicion.componentes,
                lateralidad: medicion.lateralidad,
                metodo: medicion.metodo,
            });
            // El lado del campo manda (F6.1): un valor de OI en el campo de OD es un error.
            if (campo.lado !== undefined && medicion.lateralidad !== campo.lado) {
                problemas.push({ campo: 'lateralidad', mensaje: 'el lado no es el de este campo' });
            }
            return problemas.length > 0 ? problemas.map((p) => p.mensaje).join('; ') : null;
        }
        case 'tabla': {
            if (!Array.isArray(valor))
                return 'debe ser una lista de filas';
            if (valor.length > campo.maxFilas)
                return `admite hasta ${campo.maxFilas} filas`;
            for (const [indice, fila] of valor.entries()) {
                if (!fila || typeof fila !== 'object')
                    return `la fila ${indice + 1} no es válida`;
                const celdas = fila;
                for (const clave of Object.keys(celdas)) {
                    if (!campo.columnas.some((c) => c.id === clave))
                        return `columna desconocida: ${clave}`;
                }
                for (const columna of campo.columnas) {
                    const celda = celdas[columna.id];
                    if (vacio(celda))
                        continue;
                    const problema = problemaDeValor(columna, celda);
                    if (problema)
                        return `fila ${indice + 1}, ${columna.etiqueta}: ${problema}`;
                }
            }
            return null;
        }
        case 'adjunto':
            if (!Array.isArray(valor) ||
                valor.some((v) => typeof v !== 'string' || !/^[0-9a-f]{24}$/i.test(v))) {
                return 'debe ser una lista de archivos';
            }
            return valor.length > campo.maximo ? `admite hasta ${campo.maximo} archivos` : null;
    }
}
/**
 * Que no cuadra de unas respuestas contra su plantilla. Exportado para que la
 * interfaz y el servidor digan lo mismo.
 *
 * - Una respuesta a un campo que la plantilla no tiene, o que esta oculto por
 *   su condicion, es un error: no se guardan datos fuera de la semantica.
 * - `paraFirmar` exige ademas los campos requeridos que esten visibles.
 */
export function problemasDeRespuestas(plantilla, respuestas, opciones) {
    const problemas = [];
    const conocidos = new Map();
    for (const seccion of plantilla.secciones) {
        const seccionVisible = !seccion.condicion || evaluarCondicion(seccion.condicion, respuestas);
        for (const campo of seccion.campos) {
            const visible = seccionVisible && (!campo.condicion || evaluarCondicion(campo.condicion, respuestas));
            conocidos.set(campo.id, { campo, visible });
        }
    }
    for (const [clave, valor] of Object.entries(respuestas)) {
        const conocido = conocidos.get(clave);
        if (!conocido) {
            problemas.push(`Campo desconocido: ${clave}`);
            continue;
        }
        if (vacio(valor))
            continue;
        if (!conocido.visible) {
            problemas.push(`${conocido.campo.etiqueta}: no aplica con las respuestas actuales`);
            continue;
        }
        const problema = problemaDeValor(conocido.campo, valor);
        if (problema)
            problemas.push(`${conocido.campo.etiqueta}: ${problema}`);
    }
    if (opciones.paraFirmar) {
        for (const { campo, visible } of conocidos.values()) {
            if (visible && campo.requeridoParaFirmar && vacio(respuestas[campo.id])) {
                problemas.push(`Falta para firmar: ${campo.etiqueta}`);
            }
        }
    }
    return problemas;
}
/**
 * Copiar una visita (F6.1): un borrador nuevo con las respuestas de otro
 * documento de la MISMA paciente y la misma version de plantilla. Las
 * mediciones solo se copian si se pide: un valor de la visita anterior no es
 * una medicion de hoy.
 */
export const esquemaCopiarDocumentoClinico = z
    .object({ encuentroId: esquemaId, incluirMediciones: z.boolean().default(false) })
    .strict();
/**
 * Las respuestas que pasan a la copia, campo por campo y con el MISMO
 * identificador: cada valor queda en su campo, y como cada campo por ojo tiene
 * su lado fijo, un valor de OD no puede caer en OI. No se reacomoda nada.
 * Los adjuntos no se copian (son de la otra visita) y las mediciones, solo si
 * se pide.
 */
export function respuestasParaCopia(plantilla, respuestas, opciones) {
    const copia = {};
    const omitidos = [];
    for (const campo of plantilla.secciones.flatMap((seccion) => seccion.campos)) {
        const valor = respuestas[campo.id];
        if (vacio(valor))
            continue;
        const omitir = campo.tipo === 'adjunto' || (campo.tipo === 'observacion' && !opciones.incluirMediciones);
        if (omitir)
            omitidos.push(campo.id);
        else
            copia[campo.id] = structuredClone(valor);
    }
    return { respuestas: copia, omitidos };
}
/**
 * Indice comun de los documentos de una paciente (F2.7, plan maestro §13.2).
 *
 * Normaliza tipo, estado y referencias de los documentos que ya existian
 * (historia, colposcopia, recetas, notas) y de los documentos con plantilla,
 * SIN tocarlos: cada entrada se lee de su propio modelo, que conserva su forma y
 * su firma. Para abrir uno se usa su propia ruta (`ruta`).
 */
export const TIPOS_DE_INDICE_DOCUMENTAL = [
    'historia-clinica',
    'colposcopia',
    'receta',
    'nota-evolucion',
    'nota-egreso',
    'nota-aclaratoria',
    'documento-clinico',
];
//# sourceMappingURL=documentoClinico.js.map